import { FC, useEffect } from 'react';
import { useSelector } from 'react-redux';

import { Col, Empty, Row, Spin } from 'antd';
import dayjs from 'dayjs';

import EyeAttachmentPreview from 'components/EyeAttachmentPreview/EyeAttachmentPreview';
import SidebarNavigation from 'components/SidebarNavigation';

import {
  selectRecruitmentOverviewClosedHires,
  selectRecruitmentOverviewClosedHiresRequestStatus,
  selectRecruitmentOverviewJobs
} from 'features/reports/reportsSelectors';
import { fetchRecruitmentOverviewReportClosedHires } from 'features/reports/reportsThunks';
import { selectActiveUserId } from 'features/sidebar/sidebarSelectors';
import { closeSidebar, setActiveUserId } from 'features/sidebar/sidebarSlice';

import useErrorCatch from 'hooks/useErrorCatch';
import { useAppDispatch } from 'hooks/useTypedRedux';

import { JobseekerAttachmentTypes, RequestStatus } from 'constants/enums';
import {
  DATE_FORMAT,
  FALLBACK_NUMBER_VALUE,
  FALLBACK_VALUE
} from 'constants/uiSettings';

import { useTranslation } from 'react-i18next';
import styles from './ClosedHiresView.module.scss';

const ClosedHiresView: FC = () => {
  const { t } = useTranslation()
  const dispatch = useAppDispatch();
  const catchError = useErrorCatch();

  const activeJobId = useSelector(selectActiveUserId);
  const jobs = useSelector(selectRecruitmentOverviewJobs);
  const closedHires = useSelector(selectRecruitmentOverviewClosedHires);
  const requestStatus = useSelector(
    selectRecruitmentOverviewClosedHiresRequestStatus
  );

  const currentClient = jobs.find((job) => job.jobId === activeJobId);
  const isLoading = requestStatus === RequestStatus.Loading;

  const loadClosedHires = async (jobId: string) => {
    try {
      await dispatch(fetchRecruitmentOverviewReportClosedHires(jobId)).unwrap();
    } catch (error) {
      catchError({
        error,
        default: { message: t('error.message.fetchHiredCandidates') }
      });

      dispatch(closeSidebar());
    }
  };

  useEffect(() => {
    loadClosedHires(activeJobId);
  }, [activeJobId]);

  return (
    <>
      <SidebarNavigation
        id={activeJobId}
        data={jobs}
        action={setActiveUserId}
        getItemId={(job) => job.jobId}
      />

      <div className={styles.heading}>
        {`Hired candidates (${currentClient?.closedHires || FALLBACK_NUMBER_VALUE})`}
      </div>

      <Row gutter={[16, 16]}>
        {closedHires.map((hire) => {
          return (
            <div
              className={styles.closedHiresCardContainer}
              key={`${hire.fullName}${hire.jobTitle}`}
            >
              <Col span={24}>
                {hire.fullName}
                <div className={styles.label}>{t('recruitmentOverview.name')}</div>
              </Col>

              <Col span={24}>
                {hire.jobTitle || 0}
                <div className={styles.label}>{t('recruitmentOverview.title')}</div>
              </Col>

              <Col span={12}>
                {hire?.cv ? (
                  <EyeAttachmentPreview
                    attachments={[hire.cv]}
                    attachmentType={JobseekerAttachmentTypes.CV}
                  />
                ) : (
                  FALLBACK_VALUE
                )}
                <div className={styles.label}>{t('recruitmentOverview.resume')}</div>
              </Col>

              <Col span={12}>
                {hire.startDate
                  ? dayjs(hire.startDate).format(DATE_FORMAT)
                  : FALLBACK_VALUE}
                <div className={styles.label}>{t('recruitmentOverview.pendingOffers')}</div>
              </Col>
            </div>
          );
        })}
      </Row>
      {isLoading && <Spin className={styles.loadingSpinner} />}

      {closedHires.length === 0 && !isLoading && (
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
      )}
    </>
  );
};

export default ClosedHiresView;
